import React, { useState, useEffect, useRef } from 'react';
import { AlertCircle, Award, Clock, Zap, Download, Upload, Trash, Star } from 'lucide-react';
import './App.css'; // CSS 파일 import

const App = () => {
  // Game states
  const [level, setLevel] = useState(1); // 1-10 levels
  const [gameState, setGameState] = useState('menu'); // menu, playing, result, settings
  const [currentText, setCurrentText] = useState('');
  const [userInput, setUserInput] = useState('');
  const [score, setScore] = useState(0);
  const [timer, setTimer] = useState(60);
  const [accuracy, setAccuracy] = useState(100);
  const [wpm, setWpm] = useState(0);
  const [totalTyped, setTotalTyped] = useState(0);
  const [totalCorrectTyped, setTotalCorrectTyped] = useState(0);
  const [totalMistakes, setTotalMistakes] = useState(0);
  const [finalStats, setFinalStats] = useState({
    finalWpm: 0,
    finalAccuracy: 100,
    finalMistakes: 0
  });
  const [customTexts, setCustomTexts] = useState(null);
  const [jsonError, setJsonError] = useState('');
  
  const inputRef = useRef(null);
  const fileInputRef = useRef(null);
  const intervalRef = useRef(null);
  
  // Default text collections by level
  const defaultTexts = {
    1: [
      "The quick brown fox jumps over the lazy dog.",
      "How vexingly quick daft zebras jump!",
      "Pack my box with five dozen liquor jugs.",
      "Amazingly few discotheques provide jukeboxes.",
      "Crazy Fredrick bought many very exquisite opal jewels."
    ],
    2: [
      "The five boxing wizards jump quickly.",
      "We promptly judged antique ivory buckles for the next prize.",
      "The job requires extra pluck and zeal from every young wage earner.",
      "A quivering Texas zombie fought republic linked jewelry.",
      "Six big devils from Japan quickly forgot how to waltz."
    ],
    // 나머지 레벨의 텍스트는 간결함을 위해 생략했습니다. 실제 구현 시 모든 레벨 추가 필요
    10: [
      "The synthesis of novel supramolecular assemblies exhibiting unprecedented photocatalytic properties has significant implications for renewable energy applications.",
      "Paleoclimatological reconstructions derived from analysis of Antarctic ice cores provide compelling evidence for abrupt temperature fluctuations.",
      "Recent advancements in topological quantum field theory have facilitated the mathematical formulation of previously intractable problems.",
      "The comprehensive meta-analysis of longitudinal epidemiological studies revealed subtle yet statistically significant associations.",
      "Computational linguistics researchers have developed sophisticated natural language processing algorithms capable of identifying subtle syntactic patterns."
    ]
  };
  
  // 중간 레벨의 텍스트 추가 (3-9)
  for (let i = 3; i <= 9; i++) {
    defaultTexts[i] = [
      `This is a sample text for level ${i}. It gets progressively more difficult as the level increases.`,
      `Another example text for level ${i}. Practice makes perfect!`,
      `Keep typing to improve your speed and accuracy at level ${i}.`,
      `The more you practice, the better you'll become at typing quickly and accurately.`,
      `Level ${i} text to help you master your keyboard skills.`
    ];
  }
  
  // Load texts from localStorage on component mount
  useEffect(() => {
    try {
      const savedTexts = localStorage.getItem('typingGameTexts');
      if (savedTexts) {
        setCustomTexts(JSON.parse(savedTexts));
      }
    } catch (error) {
      console.error('Error loading saved texts:', error);
    }
  }, []);
  
  // Get the appropriate texts based on if custom texts exist
  const getTexts = (selectedLevel) => {
    if (customTexts && customTexts[selectedLevel] && customTexts[selectedLevel].length > 0) {
      return customTexts[selectedLevel];
    }
    return defaultTexts[selectedLevel];
  };
  
  // Save custom texts to localStorage
  const saveTextsToLocalStorage = (texts) => {
    try {
      // Make sure we're saving a string
      const textsString = JSON.stringify(texts);
      // Test if localStorage is available and working
      localStorage.setItem('typingGameTextsTest', 'test');
      localStorage.removeItem('typingGameTextsTest');
      
      localStorage.setItem('typingGameTexts', textsString);
      setCustomTexts(texts);
    } catch (error) {
      console.error('Error saving texts:', error);
      setJsonError('텍스트 저장 중 오류가 발생했습니다: ' + error.message);
      // If localStorage is full, try to clear it
      if (error.name === 'QuotaExceededError') {
        setJsonError('저장 공간이 부족합니다. 브라우저 캐시를 비우고 다시 시도해주세요.');
      }
    }
  };
  
  // Export texts as JSON file
  const exportTexts = () => {
    try {
      // Always use a combination of custom and default texts
      // If customTexts exists, use it for those levels, otherwise use defaultTexts
      const textsToExport = {};
      
      for (let i = 1; i <= 10; i++) {
        if (customTexts && customTexts[i] && customTexts[i].length > 0) {
          textsToExport[i] = customTexts[i];
        } else {
          textsToExport[i] = defaultTexts[i];
        }
      }
      
      const dataStr = JSON.stringify(textsToExport, null, 2);
      const blob = new Blob([dataStr], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      
      const exportFileDefaultName = 'typing_game_texts.json';
      
      const linkElement = document.createElement('a');
      linkElement.href = url;
      linkElement.download = exportFileDefaultName;
      document.body.appendChild(linkElement); // Required for Firefox
      linkElement.click();
      
      // Clean up
      setTimeout(() => {
        document.body.removeChild(linkElement);
        URL.revokeObjectURL(url);
      }, 100);
      
      setJsonError('');
      console.log("JSON 내보내기 완료");
    } catch (error) {
      console.error('Error exporting texts:', error);
      setJsonError('파일 내보내기 중 오류가 발생했습니다: ' + error.message);
    }
  };
  
  // Handle file upload
  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    setJsonError('');
    
    if (file) {
      const reader = new FileReader();
      
      reader.onload = (e) => {
        try {
          const uploadedData = JSON.parse(e.target.result);
          
          // Validate format
          let isValid = true;
          let missingLevels = [];
          
          for (let i = 1; i <= 10; i++) {
            if (!uploadedData[i] || !Array.isArray(uploadedData[i]) || uploadedData[i].length === 0) {
              isValid = false;
              missingLevels.push(i);
            }
          }
          
          if (isValid) {
            saveTextsToLocalStorage(uploadedData);
            setJsonError('');
            alert('텍스트가 성공적으로 로드되었습니다.');
          } else {
            setJsonError(`유효하지 않은 JSON 형식입니다. 레벨 ${missingLevels.join(', ')}에 대한 텍스트 배열이 필요합니다.`);
          }
        } catch (error) {
          console.error('Error parsing JSON file:', error);
          setJsonError('JSON 파일 파싱 중 오류가 발생했습니다: ' + error.message);
        }
      };
      
      reader.onerror = () => {
        setJsonError('파일을 읽는 중 오류가 발생했습니다.');
      };
      
      reader.readAsText(file);
      
      // Reset input value to allow uploading the same file again
      event.target.value = '';
    }
  };
  
  // Reset custom texts
  const resetTexts = () => {
    try {
      localStorage.removeItem('typingGameTexts');
      setCustomTexts(null);
      setJsonError('');
      // Provide feedback that the reset was successful
      alert('텍스트가 기본값으로 초기화되었습니다.');
      console.log("텍스트 초기화 완료");
    } catch (error) {
      console.error('Error resetting texts:', error);
      setJsonError('텍스트 초기화 중 오류가 발생했습니다: ' + error.message);
    }
  };
  
  // Start game function
  const startGame = () => {
    // Reset game stats
    setUserInput('');
    setScore(0);
    setTimer(60);
    setAccuracy(100);
    setWpm(0);
    setTotalTyped(0);
    setTotalCorrectTyped(0);
    setTotalMistakes(0);
    setFinalStats({
      finalWpm: 0,
      finalAccuracy: 100,
      finalMistakes: 0
    });
    
    // Set the current timestamp as starting point
    const now = Date.now();
    setStartTime(now);
    
    // Set a random text based on level
    const textsForLevel = getTexts(level);
    const randomIndex = Math.floor(Math.random() * textsForLevel.length);
    setCurrentText(textsForLevel[randomIndex]);
    
    // Change game state to playing
    setGameState('playing');
    
    // Focus on input field
    setTimeout(() => {
      if (inputRef.current) {
        inputRef.current.focus();
      }
    }, 100);
    
    // Start timer
    intervalRef.current = setInterval(() => {
      setTimer(prevTimer => {
        if (prevTimer <= 1) {
          endGame();
          return 0;
        }
        return prevTimer - 1;
      });
    }, 1000);
  };
  
  // End game function
  const endGame = () => {
    // Clear timer
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }
    
    // Calculate current mistakes in the input
    const currentInputMistakes = userInput.split('').filter((char, index) => 
      char !== currentText[index]
    ).length;
    
    const updatedTotalTyped = totalTyped + userInput.length;
    const updatedTotalMistakes = totalMistakes + currentInputMistakes;
    const updatedTotalCorrectTyped = totalCorrectTyped + (userInput.length - currentInputMistakes);
    
    // Calculate game duration in minutes
    const timeTaken = (60 - timer) / 60; // in minutes (based on actual time played)
    
    // Calculate final stats
    let finalStats = {
      finalWpm: 0,
      finalAccuracy: 100,
      finalMistakes: updatedTotalMistakes
    };
    
    // Ensure we don't divide by zero
    if (timeTaken > 0) {
      // WPM calculation based on total characters typed
      finalStats.finalWpm = Math.round((updatedTotalTyped / 5) / timeTaken);
      
      // Accuracy calculation
      finalStats.finalAccuracy = updatedTotalTyped > 0 
        ? Math.round((updatedTotalCorrectTyped / updatedTotalTyped) * 100) 
        : 100;
    }
    
    // Update state with final stats
    setFinalStats(finalStats);
    
    // Update game state
    setGameState('result');
  };
  
  // Handle user input
  const handleInputChange = (e) => {
    const value = e.target.value;
    setUserInput(value);
    
    // Check for mistakes
    let currentMistakes = 0;
    for (let i = 0; i < value.length; i++) {
      if (value[i] !== currentText[i]) {
        currentMistakes++;
      }
    }
    
    // Calculate accuracy
    const accuracyValue = value.length > 0
      ? Math.max(0, Math.floor(100 - (currentMistakes / value.length * 100)))
      : 100;
    setAccuracy(accuracyValue);
    
    // Update WPM in real-time
    if (startTime) {
      const minutes = (Date.now() - startTime) / 1000 / 60;
      const words = value.length / 5;
      if (minutes > 0) {
        // Ensure we get a more accurate real-time WPM
        const currentWpm = Math.round(words / minutes);
        setWpm(currentWpm > 0 ? currentWpm : 0);
      }
    }
    
    // Check if completed
    if (value === currentText) {
      // Update total character and mistake counts
      setTotalTyped(prev => prev + currentText.length);
      setTotalCorrectTyped(prev => prev + (currentText.length - currentMistakes));
      setTotalMistakes(prev => prev + currentMistakes);
      
      // Calculate score based on accuracy, wpm and level multiplier
      const levelMultiplier = level / 2; // Increases with level
      
      // Calculate character speed for this completion
      const typingTimeMinutes = (Date.now() - startTime) / 1000 / 60; // time since game start in minutes
      const charsPerMinute = typingTimeMinutes > 0 ? currentText.length / typingTimeMinutes : 0;
      const completionWpm = charsPerMinute / 5; // standard WPM calculation (5 chars = 1 word)
      
      // Apply score formula with current completion stats
      const newScore = Math.round(accuracy * completionWpm * levelMultiplier);
      setScore(prevScore => prevScore + newScore);
      
      // Get next text
      const textsForLevel = getTexts(level);
      const currentIndex = textsForLevel.indexOf(currentText);
      const nextIndex = (currentIndex + 1) % textsForLevel.length;
      setCurrentText(textsForLevel[nextIndex]);
      
      // Reset input and update start time for the next text
      setUserInput('');
      setStartTime(Date.now());
    }
  };
  
  // Render text with highlighting
  const renderText = () => {
    const correct = [];
    const incorrect = [];
    const remaining = [];
    
    for (let i = 0; i < currentText.length; i++) {
      if (i < userInput.length) {
        if (userInput[i] === currentText[i]) {
          correct.push(currentText[i]);
        } else {
          incorrect.push(currentText[i]);
        }
      } else {
        remaining.push(currentText[i]);
      }
    }
    
    return (
      <div className="typing-text">
        <span className="text-correct">{correct.join('')}</span>
        <span className="text-incorrect">{incorrect.join('')}</span>
        <span className="text-remaining">{remaining.join('')}</span>
      </div>
    );
  };
  
  // Handle level change
  const changeLevel = (newLevel) => {
    setLevel(newLevel);
  };
  
  // Return to menu
  const returnToMenu = () => {
    setGameState('menu');
  };
  
  // Go to settings
  const goToSettings = () => {
    setGameState('settings');
  };
  
  // Render level selection buttons
  const renderLevelButtons = () => {
    return (
      <div className="level-selection">
        {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((lvl) => (
          <button
            key={lvl}
            className={`level-btn ${level === lvl ? 'active' : ''}`}
            onClick={() => changeLevel(lvl)}
          >
            <span>{lvl}단계</span>
            {lvl <= 3 && <span className="level-star">★</span>}
            {lvl > 3 && lvl <= 6 && (
              <>
                <span className="level-star">★★</span>
              </>
            )}
            {lvl > 6 && (
              <>
                <span className="level-star">★★★</span>
              </>
            )}
          </button>
        ))}
      </div>
    );
  };
  
  // State for start time
  const [startTime, setStartTime] = useState(null);
  
  // Clean up on unmount
  useEffect(() => {
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, []);
  
  return (
    <div className="app-container">
      <div className="game-header">
        <h1 className="game-title">영어 타자 연습 게임</h1>
        <p className="game-subtitle">재미있게 타이핑 연습을 해보세요!</p>
      </div>
      
      {gameState === 'menu' && (
        <div className="flex flex-col items-center">
          <h2 className="text-2xl font-semibold mb-4">난이도 선택</h2>
          {renderLevelButtons()}
          
          <div className="text-center mb-8">
            <h3 className="font-semibold mb-2">난이도 설명:</h3>
            <p className="mb-1"><span className="font-bold">1-3단계:</span> 짧은 문장, 기본 단어</p>
            <p className="mb-1"><span className="font-bold">4-6단계:</span> 중간 길이 문장, 다양한 단어</p>
            <p className="mb-1"><span className="font-bold">7-10단계:</span> 긴 문장, 어려운 단어와 구두점</p>
          </div>
          
          <div className="flex gap-4">
            <button
              className="btn btn-primary"
              onClick={startGame}
            >
              게임 시작
            </button>
            <button
              className="btn btn-secondary"
              onClick={goToSettings}
            >
              텍스트 설정
            </button>
          </div>
        </div>
      )}
      
      {gameState === 'settings' && (
        <div>
          <h2 className="text-2xl font-semibold mb-4 text-center">텍스트 설정</h2>
          
          <div className="settings-container">
            <div className="settings-actions">
              <button
                className="btn btn-primary"
                onClick={() => {
                  exportTexts();
                  console.log('Export button clicked');
                }}
              >
                <Download className="btn-icon" size={20} />
                JSON 내보내기
              </button>
              
              <button
                className="btn btn-secondary"
                onClick={() => {
                  console.log('Upload button clicked');
                  if (fileInputRef.current) {
                    fileInputRef.current.click();
                  } else {
                    setJsonError('파일 업로드 컴포넌트를 찾을 수 없습니다.');
                  }
                }}
              >
                <Upload className="btn-icon" size={20} />
                JSON 불러오기
              </button>
              
              <input 
                type="file" 
                ref={fileInputRef} 
                className="hidden" 
                accept=".json" 
                onChange={handleFileUpload} 
              />
              
              <button
                className="btn btn-danger"
                onClick={() => {
                  resetTexts();
                  console.log('Reset button clicked');
                }}
              >
                <Trash className="btn-icon" size={20} />
                기본값으로 리셋
              </button>
            </div>
            
            {jsonError && (
              <div className="settings-error">
                {jsonError}
              </div>
            )}
            
            <div className="settings-info">
              <h3 className="settings-title">JSON 형식 안내:</h3>
              <pre className="json-example">
{`{
  "1": [ "1단계 텍스트 1", "1단계 텍스트 2", ... ],
  "2": [ "2단계 텍스트 1", "2단계 텍스트 2", ... ],
  ...
  "10": [ "10단계 텍스트 1", "10단계 텍스트 2", ... ]
}`}
              </pre>
            </div>
            
            <p className="settings-note">
              * JSON 파일을 업로드하면 모든 난이도의 텍스트가 업데이트됩니다.<br />
              * 텍스트는 브라우저의 localStorage에 저장되며, 기기간 동기화되지 않습니다.
            </p>
          </div>
          
          <div className="flex justify-center">
            <button
              className="btn btn-neutral"
              onClick={returnToMenu}
            >
              메뉴로 돌아가기
            </button>
          </div>
        </div>
      )}
      
      {gameState === 'playing' && (
        <div>
          <div className="game-stats">
            <div className="stat-item">
              <Clock className="stat-icon" size={20} />
              <span>{timer}초</span>
            </div>
            <div className="stat-item">
              <Award className="stat-icon" size={20} />
              <span>점수: {score}</span>
            </div>
            <div className="stat-item">
              <Zap className="stat-icon" size={20} />
              <span>WPM: {wpm}</span>
            </div>
            <div className="stat-item">
              <AlertCircle className="stat-icon" size={20} />
              <span>정확도: {accuracy}%</span>
            </div>
          </div>
          
          <div className="typing-area">
            {renderText()}
          </div>
          
          <input
            ref={inputRef}
            type="text"
            value={userInput}
            onChange={handleInputChange}
            className="typing-input"
            placeholder="여기에 타이핑하세요..."
          />
        </div>
      )}
      
      {gameState === 'result' && (
        <div className="text-center">
          <h2 className="result-title">게임 결과</h2>
          
          <div className="result-grid">
            <div className="result-card">
              <h3 className="result-label">최종 점수</h3>
              <p className="result-value score">{score}</p>
            </div>
            <div className="result-card">
              <h3 className="result-label">타자 속도</h3>
              <p className="result-value wpm">{finalStats.finalWpm} WPM</p>
            </div>
            <div className="result-card">
              <h3 className="result-label">정확도</h3>
              <p className="result-value accuracy">{finalStats.finalAccuracy}%</p>
            </div>
            <div className="result-card">
              <h3 className="result-label">오타</h3>
              <p className="result-value mistakes">{finalStats.finalMistakes}</p>
            </div>
          </div>
          
          <div className="result-actions">
            <button
              className="btn btn-primary"
              onClick={startGame}
            >
              다시 시작
            </button>
            <button
              className="btn btn-neutral"
              onClick={returnToMenu}
            >
              메뉴로 돌아가기
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default App;